'use client'

import { Fragment, useEffect, useRef, useState, type CSSProperties } from 'react'
import Link from 'next/link'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useTicker } from '@/hooks/useShellData'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Icons } from '@/components/ui/icons'
import type { TickerItem } from '@/lib/dr-types'

function itemKey(item: TickerItem): string {
  return `${item.who}|${item.ref ?? ''}|${item.text}`
}

function Line({ who, text }: { who: string; text: string }) {
  return (
    <span className='whitespace-nowrap'>
      <b className='mr-1.5 font-semibold text-brand-cornflower-light'>{who}</b>
      {text}
    </span>
  )
}

/**
 * The scrolling band on the Auto Workbench and the Operations Globe (plans/03
 * A5.4, TDD D28): "LIVE", the event line, then what is arriving and what the
 * AI Employees are doing. Not rendered before the earthquake.
 *
 * Accessibility (Rule 14): the moving track is hidden from assistive
 * technology; a polite live region announces each newly arrived item once;
 * "Show all" lists every line with its link. Hover or focus pauses the band,
 * and with reduced motion it does not move.
 */
export function Ticker({ className }: { className?: string }) {
  const ticker = useTicker()
  const reduced = useReducedMotion()
  const [announcement, setAnnouncement] = useState('')
  const [paused, setPaused] = useState(false)
  const newestRef = useRef<string | null>(null)

  const newest = ticker?.items[0] ?? null
  const newestId = newest ? itemKey(newest) : null

  useEffect(() => {
    if (!newest || !newestId) return
    if (newestRef.current !== null && newestRef.current !== newestId) {
      setAnnouncement(`${newest.who}: ${newest.text}`)
    }
    newestRef.current = newestId
  }, [newest, newestId])

  if (!ticker || !ticker.head) return null

  const lines: { who: string; text: string }[] = [ticker.head, ...ticker.items]
  const one = (copy: number) =>
    lines.map((line, index) => (
      <Fragment key={`${copy}-${index}`}>
        {index > 0 && (
          <span className='opacity-50' aria-hidden='true'>
            •
          </span>
        )}
        <Line who={line.who} text={line.text} />
      </Fragment>
    ))

  const style = { '--ticker-duration': `${Math.max(10, ticker.speed_seconds || 60)}s` } as CSSProperties

  return (
    <div
      role='region'
      aria-label='Live updates'
      className={cn(
        'aio-ticker mb-4 flex items-center gap-3 overflow-hidden rounded-xl bg-primary px-3 py-2 text-sm text-primary-foreground',
        className
      )}
      style={style}
    >
      <span className='inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-widest'>
        <span aria-hidden='true' className='h-2 w-2 rounded-full bg-needs-you-soft ring-2 ring-needs-you-soft/30' />
        Live
      </span>

      <div className='aio-ticker-window min-w-0 flex-1 overflow-hidden' aria-hidden='true'>
        {reduced ? (
          <div className='flex gap-3 truncate'>
            <Line who={ticker.head.who} text={ticker.head.text} />
            {newest && (
              <>
                <span className='opacity-50'>•</span>
                <Line who={newest.who} text={newest.text} />
              </>
            )}
          </div>
        ) : (
          <div
            className='aio-ticker-track inline-flex gap-3.5'
            style={paused ? { animationPlayState: 'paused' } : undefined}
          >
            {one(0)}
            <span className='opacity-50'>•</span>
            {one(1)}
          </div>
        )}
      </div>

      {!reduced && (
        <button
          type='button'
          aria-pressed={paused}
          aria-label='Pause live updates'
          onClick={() => setPaused((value) => !value)}
          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
        >
          {paused ? (
            <Icons.play className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
          ) : (
            <Icons.pause className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
          )}
        </button>
      )}

      <Popover>
        <PopoverTrigger asChild>
          <button
            type='button'
            className='shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
          >
            Show all
          </button>
        </PopoverTrigger>
        <PopoverContent align='end' className='w-96 max-w-[calc(100vw-2rem)] p-3'>
          <p className='mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground'>Live updates</p>
          <ul className='max-h-80 space-y-2 overflow-y-auto text-sm'>
            {lines.map((line, index) => {
              const item = index > 0 ? ticker.items[index - 1] : null
              const body = (
                <>
                  <span className='font-semibold text-foreground'>{line.who}</span>{' '}
                  <span className='text-foreground'>{line.text}</span>
                  {item?.at_label && <span className='text-muted-foreground'> · {item.at_label}</span>}
                </>
              )
              return (
                <li key={index}>
                  {item?.href ? (
                    <Link
                      href={item.href}
                      className='block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                </li>
              )
            })}
          </ul>
        </PopoverContent>
      </Popover>

      <span className='sr-only' aria-live='polite'>
        {announcement}
      </span>
    </div>
  )
}
